/**
 * Questions tab: list + detail + add control.
 * — lg+: left column = list + add under list (inside sidebar); right column = detail.
 * — Small: all questions/sections stacked when `renderItemDetail` is set;
 *   add under stack; list column hidden.
 *
 * `sidebarFooter` is mounted twice (responsive wrappers). Prefer
 * **`CompositeEditSortableQuestionsLayout`** for the common sortable sidebar +
 * add button + detail shell. Sidebar reorder is **drag and drop**; **click / drag**
 * use the full `{prefix}-sidebar-item-header` row (remove button excluded).
 */

import * as React from 'react';
import filter from 'lodash/filter';
import map from 'lodash/map';
import {
    Badge,
    Button,
    Card,
    CardContent,
    cn,
    DndContext,
    KeyboardSensor,
    PointerSensor,
    SortableContext,
    SortableItem,
    useDroppable,
    useSensor,
    useSensors,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    type DragEndEvent,
    arrayMove,
    createEmptyDocument,
    RichTextEditor,
    Input,
    Label,
    type TiptapJSON,
    type KitTranslateFn,
} from '@ai-content-hub/content-kit';
import type { CollisionDetection, DragOverEvent, DragStartEvent } from '@dnd-kit/core';
import { X } from 'lucide-react';

/**
 * Collision detection that only considers vertical (Y-axis) distance.
 * This allows items nested under a section (indented via ml-6) to be dragged
 * past section headers without the horizontal offset biasing the target.
 */
const verticalClosestCollision: CollisionDetection = (args) => {
    const { droppableRects, droppableContainers, active } = args;
    const activeRect = active.rect.current.translated;
    if (!activeRect) return [];

    const activeCenterY = activeRect.top + activeRect.height / 2;
    let closest: { id: string | number; distance: number } | null = null;

    for (const container of droppableContainers) {
        const rect = droppableRects.get(container.id);
        if (!rect) continue;
        const containerCenterY = rect.top + rect.height / 2;
        const distance = Math.abs(activeCenterY - containerCenterY);
        if (!closest || distance < closest.distance) {
            closest = { id: container.id, distance };
        }
    }

    return closest ? [{ id: closest.id, data: { droppableContainer: droppableContainers.find(c => c.id === closest!.id) } }] : [];
};
import {
    CompositeEditAddQuestionButton,
    type CompositeEditAddQuestionOption,
} from './CompositeEditAddQuestionButton';
import {
    compositeEditQuestionSidebarSortableId,
    isSidebarItemNestedUnderSection,
    sectionSidebarDescriptionPreview,
    tiptapDocToPlainText,
    type CompositeEditQuestionSidebarSection,
    type CompositeEditQuestionSidebarRow,
} from './compositeQuestionSidebarRows';


export type {
    CompositeEditQuestionSidebarSection,
    CompositeEditQuestionSidebarRow,
    CompositeQuestionSidebarExerciseGroup,
} from './compositeQuestionSidebarRows';
export {
    buildQuestionSidebarExerciseGroups,
    compositeEditQuestionSidebarSortableId,
    deriveDefaultQuestionsSidebarRows,
    isSidebarItemNestedUnderSection,
    orderItemsByQuestionsSidebarRows,
    persistQuestionsSidebarRows,
    reconcileQuestionsSidebarRows,
    resolvedQuestionsSidebarRows,
    sectionSidebarDescriptionPreview,
    tiptapDocToPlainText,
} from './compositeQuestionSidebarRows';

type CompositeEditQuestionsLayoutSplit = {
    prefix: string;
    className?: string;
    sidebarWidthClassName?: string;
    variant?: 'split';
    /** Header, empty state, sortable list — `lg`+ only (left column). */
    sidebarList: React.ReactNode;
    /** Add control — under `sidebarList` on `lg+`; below detail on small screens only. */
    sidebarFooter: React.ReactNode;
    detail: React.ReactNode;
};

type CompositeEditQuestionsLayoutDetailOnly = {
    prefix: string;
    className?: string;
    variant: 'detail-only';
    detail: React.ReactNode;
};

export type CompositeEditQuestionsLayoutProps =
    | CompositeEditQuestionsLayoutSplit
    | CompositeEditQuestionsLayoutDetailOnly;

export function CompositeEditQuestionsLayout(
    props: CompositeEditQuestionsLayoutProps
): React.ReactElement {
    if (props.variant === 'detail-only') {
        const { prefix, className, detail } = props;
        return (
            <div
                className={cn(
                    `${prefix}-questions`,
                    `${prefix}-questions--detail-only`,
                    'flex min-h-0 flex-1',
                    className
                )}
            >
                <div
                    className={cn(
                        `${prefix}-detail`,
                        'flex min-h-0 min-w-0 flex-1 flex-col border-0 shadow-sm'
                    )}
                >
                    <div
                        className={cn(
                            `${prefix}-detail-body p-1`,
                            'min-h-0 flex-1 overflow-y-auto'
                        )}
                    >
                        {detail}
                    </div>
                </div>
            </div>
        );
    }

    const {
        prefix,
        className,
        sidebarWidthClassName = 'max-w-xs',
        sidebarList,
        sidebarFooter,
        detail,
    } = props;

    return (
        <div
            className={cn(
                `${prefix}-questions`,
                'grid min-h-0 flex-1 gap-4',
                'grid-cols-1 grid-rows-[minmax(0,1fr)_auto]',
                'lg:grid-cols-[minmax(0,max-content)_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)]',
                className
            )}
        >
            <Card
                className={cn(
                    `${prefix}-sidebar`,
                    'hidden min-h-0 flex-col border-0 shadow-sm lg:col-start-1 lg:row-start-1 lg:flex lg:h-full lg:min-h-0',
                    sidebarWidthClassName
                )}
            >
                <CardContent
                    className={cn(
                        `${prefix}-sidebar-body`,
                        'flex min-h-0 flex-1 flex-col gap-2 overflow-hidden p-3'
                    )}
                >
                    <div
                        className={cn(
                            `${prefix}-sidebar-list`,
                            'flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto'
                        )}
                    >
                        {sidebarList}
                        <div
                            className={cn(
                                `${prefix}-questions-sidebar-footer`,
                                'hidden shrink-0 lg:block pb-4'
                            )}
                        >
                            {sidebarFooter}
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card
                className={cn(
                    `${prefix}-detail`,
                    'col-start-1 row-start-1 min-h-0 border-0 shadow-sm lg:col-start-2 lg:row-start-1',
                    'flex flex-col'
                )}
            >
                <CardContent
                    className={cn(`${prefix}-detail-body`, 'min-h-0 flex-1 overflow-y-auto pt-6')}
                >
                    {detail}
                </CardContent>
            </Card>

            <div
                className={cn(
                    `${prefix}-questions-bottom`,
                    'col-start-1 row-start-2 shrink-0 lg:hidden pb-4'
                )}
            >
                {sidebarFooter}
            </div>
        </div>
    );
}

// --- Sortable list + add + detail (common Questions tab) ---

type SortableSectionRowProps = {
    prefix: string;
    sectionId: string;
    sectionTitle: string;
    descPreview: string;
    descPlainText: string;
    isSelected: boolean;
    disabled: boolean;
    removeLabel: string;
    /** Stable callback: receives sectionId so no per-row closure needed. */
    onSelectSection: (sectionId: string) => void;
    /** Stable callback: receives sectionId so no per-row closure needed. */
    onRemoveSection: (sectionId: string) => void;
    /** Label shown when section title is empty */
    untitledSectionLabel: string;
};

const SortableSectionRow: React.FC<SortableSectionRowProps> = React.memo(function SortableSectionRow({
    prefix,
    sectionId,
    sectionTitle,
    descPreview,
    descPlainText,
    isSelected,
    disabled,
    removeLabel,
    onSelectSection,
    onRemoveSection,
    untitledSectionLabel,
}) {

    return (
        <SortableItem
            id={`__section:${sectionId}`}
            disabled={disabled}
        >
            {({ setNodeRef, style, isDragging, attributes, listeners }) => (
                <div
                    ref={setNodeRef}
                    style={style}
                    className={cn(
                        `${prefix}-sidebar-section`,
                        'rounded-lg border border-border overflow-hidden transition-shadow',
                        isSelected && 'ring-2 ring-primary/25 shadow-sm',
                        isDragging && 'opacity-70 z-10'
                    )}
                >
                    <div
                        className={cn(
                            `${prefix}-sidebar-section-header`,
                            'flex items-start justify-between gap-2 px-3 py-2 bg-background',
                            'cursor-grab touch-none active:cursor-grabbing'
                        )}
                        {...listeners}
                        {...attributes}
                        onClick={() => onSelectSection(sectionId)}
                    >
                        <div
                            className={cn(
                                `${prefix}-sidebar-section-preview`,
                                'flex min-w-0 flex-1 flex-col gap-0.5 pointer-events-none'
                            )}
                        >
                            <div
                                className={cn(
                                    `${prefix}-sidebar-section-preview-main`,
                                    'flex min-w-0 items-center gap-2'
                                )}
                            >
                                <Badge
                                    variant="outline"
                                    className={cn(
                                        `${prefix}-sidebar-section-type-badge`,
                                        'shrink-0 border-primary/25 bg-primary/10 text-xs font-medium'
                                    )}
                                >
                                    Section
                                </Badge>
                                <span
                                    className={cn(
                                        `${prefix}-sidebar-section-preview-title`,
                                        'text-sm font-semibold text-foreground truncate'
                                    )}
                                >
                                    {sectionTitle.trim() ? sectionTitle : untitledSectionLabel}
                                </span>
                            </div>
                            {descPreview ? (
                                <span
                                    className={cn(
                                        `${prefix}-sidebar-section-preview-description`,
                                        'text-xs text-muted-foreground line-clamp-2 min-w-0'
                                    )}
                                    title={descPlainText}
                                >
                                    {descPreview}
                                </span>
                            ) : null}
                        </div>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className={cn(
                                `${prefix}-sidebar-section-remove`,
                                'h-8 w-8 shrink-0 cursor-pointer text-muted-foreground hover:text-foreground'
                            )}
                            disabled={disabled}
                            aria-label={removeLabel}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                                e.stopPropagation();
                                onRemoveSection(sectionId);
                            }}
                        >
                            <X className={cn(`${prefix}-sidebar-section-remove-icon`, 'h-4 w-4')} />
                        </Button>
                    </div>
                </div>
            )}
        </SortableItem>
    );
});

type SortableSidebarRowProps = {
    prefix: string;
    itemId: string;
    /** Display label, e.g. "1. Trắc nghiệm" */
    label: string;
    isSelected: boolean;
    disabled: boolean;
    hasValidationError?: boolean;
    removeDisabled: boolean;
    removeLabel: string;
    /** Stable callback: receives itemId so no per-row closure needed. */
    onSelectItem: (itemId: string) => void;
    /** Stable callback: receives itemId so no per-row closure needed. */
    onRemoveItem: (itemId: string) => void;
    nestedUnderSection?: boolean;
};

const SortableSidebarRow: React.FC<SortableSidebarRowProps> = React.memo(function SortableSidebarRow({
    prefix,
    itemId,
    label,
    isSelected,
    disabled,
    hasValidationError,
    removeDisabled,
    removeLabel,
    onSelectItem,
    onRemoveItem,
    nestedUnderSection = false,
}) {

    return (
        <SortableItem id={itemId} disabled={disabled}>
            {({ setNodeRef, style, isDragging, attributes, listeners }) => (
                <div
                    ref={setNodeRef}
                    style={style}
                    className={cn(
                        `${prefix}-sidebar-item`,
                        nestedUnderSection && `${prefix}-sidebar-item--nested`,
                        'rounded-lg border border-border overflow-hidden transition-shadow',
                        nestedUnderSection && 'ml-6',
                        isSelected && 'ring-2 ring-primary/25 shadow-sm',
                        hasValidationError && 'border-destructive/70',
                        isDragging && 'opacity-70 z-10'
                    )}
                >
                    <div
                        className={cn(
                            `${prefix}-sidebar-item-header`,
                            'flex items-center justify-between gap-2 px-3 py-2 bg-background',
                            'cursor-grab touch-none active:cursor-grabbing'
                        )}
                        {...listeners}
                        {...attributes}
                        onClick={() => onSelectItem(itemId)}
                    >
                        <div
                            className={cn(
                                `${prefix}-sidebar-item-preview`,
                                'flex min-w-0 flex-1 items-center gap-2 pointer-events-none'
                            )}
                        >
                            <span className="text-sm font-semibold text-foreground tabular-nums shrink-0">
                                {label}
                            </span>
                        </div>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className={cn(
                                `${prefix}-sidebar-item-remove`,
                                'h-8 w-8 shrink-0 cursor-pointer text-muted-foreground hover:text-foreground'
                            )}
                            disabled={disabled || removeDisabled}
                            aria-label={removeLabel}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                                e.stopPropagation();
                                onRemoveItem(itemId);
                            }}
                        >
                            <X className={cn(`${prefix}-sidebar-item-remove-icon`, 'h-4 w-4')} />
                        </Button>
                    </div>
                </div>
            )}
        </SortableItem>
    );
});
/**
 * Droppable zone rendered between adjacent sections (no root-level items in between).
 * Collapses when no drag is active; expands with a dashed border during drag.
 * Allows dropping items outside of any section.
 */
function SectionDropZone({ id, prefix, isActive, dropHereLabel = 'Drop here' }: {
    id: string;
    prefix: string;
    /** True when a drag operation is in progress. */
    isActive: boolean;
    /** Translated label for the drop zone */
    dropHereLabel?: string;
}): React.ReactElement {
    const { setNodeRef, isOver } = useDroppable({ id });
    return (
        <div
            ref={setNodeRef}
            className={cn(
                `${prefix}-sidebar-dropzone`,
                'rounded-lg transition-all overflow-hidden',
                isActive
                    ? cn(
                        'border-2 border-dashed',
                        isOver
                            ? 'border-primary bg-primary/10 py-3'
                            : 'border-muted-foreground/25 py-2',
                    )
                    : 'h-0 opacity-0',
            )}
        >
            {isActive && (
                <div className={cn(
                    `${prefix}-sidebar-dropzone-label`,
                    'text-center text-xs transition-colors',
                    isOver ? 'text-primary font-medium' : 'text-muted-foreground/50',
                )}>
                    {dropHereLabel}
                </div>
            )}
        </div>
    );
}

// --- Memoized sidebar DnD list to isolate re-renders during drag ---

type SidebarDndListProps = {
    prefix: string;
    sensors: ReturnType<typeof useSensors>;
    handleSidebarDragEnd: (event: DragEndEvent) => void;
    sortableIds: string[];
    sidebarRows: CompositeEditQuestionSidebarRow[];
    items: readonly { id: string }[];
    selectedId: string | null;
    selectedSectionId: string | null;
    disabled: boolean;
    removeBlocked: boolean;
    removeSidebarSectionLabel: string;
    removeItemLabel: string;
    itemHasValidationError?: (index: number) => boolean;
    onSelectId: (id: string | null) => void;
    onSelectSectionId: (id: string | null) => void;
    onRemoveItem: (itemId: string) => void;
    handleRemoveSection: (sectionId: string) => void;
    /** Returns a **plain string label** for each item, e.g. `"1. Trắc nghiệm"`. */
    getItemLabel: (itemId: string, index: number, total: number) => string;
    /** Label shown when section title is empty */
    untitledSectionLabel: string;
};

const SidebarDndList: React.FC<SidebarDndListProps> = React.memo(function SidebarDndList({
    prefix,
    sensors,
    handleSidebarDragEnd,
    sortableIds,
    sidebarRows,
    items,
    selectedId,
    selectedSectionId,
    disabled,
    removeBlocked,
    removeSidebarSectionLabel,
    removeItemLabel,
    itemHasValidationError,
    onSelectId,
    onSelectSectionId,
    onRemoveItem,
    handleRemoveSection,
    getItemLabel,
    untitledSectionLabel,
    dropHereLabel,
}) {

    // Pre-compute nesting map to avoid O(n²) isSidebarItemNestedUnderSection calls in the loop.
    const nestingMap = React.useMemo(() => {
        const m = new Map<number, boolean>();
        for (let i = 0; i < sidebarRows.length; i++) {
            if (sidebarRows[i].kind === 'item') {
                m.set(i, isSidebarItemNestedUnderSection(sidebarRows, i));
            }
        }
        return m;
    }, [sidebarRows]);

    // Build a lookup map so we avoid O(n) findIndex per row.
    const itemIndexMap = React.useMemo(() => {
        const m = new Map<string, number>();
        for (let i = 0; i < items.length; i++) {
            m.set(items[i].id, i);
        }
        return m;
    }, [items]);

    // --- Stable callbacks via refs so rows receive the same function identity ---
    const selectedSectionIdRef = React.useRef(selectedSectionId);
    selectedSectionIdRef.current = selectedSectionId;
    const onSelectIdRef = React.useRef(onSelectId);
    onSelectIdRef.current = onSelectId;
    const onSelectSectionIdRef = React.useRef(onSelectSectionId);
    onSelectSectionIdRef.current = onSelectSectionId;
    const onRemoveItemRef = React.useRef(onRemoveItem);
    onRemoveItemRef.current = onRemoveItem;
    const handleRemoveSectionRef = React.useRef(handleRemoveSection);
    handleRemoveSectionRef.current = handleRemoveSection;

    /** Select a section row — stable identity, safe for React.memo children. */
    const stableSelectSection = React.useCallback((sectionId: string) => {
        onSelectSectionIdRef.current(sectionId);
        onSelectIdRef.current(null);
    }, []);

    /** Remove a section row — stable identity. */
    const stableRemoveSection = React.useCallback((sectionId: string) => {
        if (selectedSectionIdRef.current === sectionId) {
            onSelectSectionIdRef.current(null);
        }
        handleRemoveSectionRef.current(sectionId);
    }, []);

    /** Select an item row — stable identity. */
    const stableSelectItem = React.useCallback((itemId: string) => {
        onSelectSectionIdRef.current(null);
        onSelectIdRef.current(itemId);
    }, []);

    /** Remove an item row — stable identity. */
    const stableRemoveItem = React.useCallback((itemId: string) => {
        onRemoveItemRef.current(itemId);
    }, []);

    // --- Drop indicator state ---
    const [activeDragId, setActiveDragId] = React.useState<string | null>(null);
    const [overDragId, setOverDragId] = React.useState<string | null>(null);

    const handleDragStart = React.useCallback((event: DragStartEvent) => {
        setActiveDragId(String(event.active.id));
    }, []);

    const handleDragOver = React.useCallback((event: DragOverEvent) => {
        setOverDragId(event.over ? String(event.over.id) : null);
    }, []);

    const handleDragEndWrapped = React.useCallback((event: DragEndEvent) => {
        setActiveDragId(null);
        setOverDragId(null);
        handleSidebarDragEnd(event);
    }, [handleSidebarDragEnd]);

    const handleDragCancel = React.useCallback(() => {
        setActiveDragId(null);
        setOverDragId(null);
    }, []);

    // Compute the visible (non-break) sortable IDs for index-based drop direction.
    const visibleSortIds = React.useMemo(() => {
        const ids: string[] = [];
        sidebarRows.forEach((row, i) => {
            if (row.kind !== 'break') {
                ids.push(compositeEditQuestionSidebarSortableId(row, i));
            }
        });
        return ids;
    }, [sidebarRows]);

    // Determine whether the indicator should appear above or below the over target.
    const activeVisibleIdx = activeDragId ? visibleSortIds.indexOf(activeDragId) : -1;
    const overVisibleIdx = overDragId ? visibleSortIds.indexOf(overDragId) : -1;
    // 'before' = indicator shows above the over target, 'after' = below
    const dropDirection: 'before' | 'after' | null =
        activeDragId && overDragId && activeDragId !== overDragId && activeVisibleIdx >= 0 && overVisibleIdx >= 0
            ? activeVisibleIdx > overVisibleIdx ? 'before' : 'after'
            : null;

    /** The drop indicator line rendered between rows. */
    const dropIndicator = (nested?: boolean) => (
        <div
            className={cn(
                `${prefix}-sidebar-drop-indicator`,
                'h-0.5 rounded-full bg-primary transition-all',
                nested && 'ml-6',
            )}
        />
    );

    /**
     * Pre-compute positions where a drop zone should appear.
     * Rules:
     * - NO zone before the first section (top of list)
     * - Zone BETWEEN two sections when no root-level items separate them
     * - Zone AFTER the last section when it has no root-level items following it
     */
    const dropZonePositions = React.useMemo(() => {
        const zones: { rowIndex: number; id: string; position: 'before' | 'end' }[] = [];
        const sectionIndices: number[] = [];
        for (let i = 0; i < sidebarRows.length; i++) {
            if (sidebarRows[i].kind === 'section') sectionIndices.push(i);
        }
        // Between adjacent sections (skip the first section — no zone at top).
        for (let s = 1; s < sectionIndices.length; s++) {
            const sIdx = sectionIndices[s];
            let hasRootBetween = false;
            for (let j = sIdx - 1; j > sectionIndices[s - 1]; j--) {
                const prev = sidebarRows[j];
                if (prev.kind === 'break') { hasRootBetween = true; break; }
                if (prev.kind === 'item' && !isSidebarItemNestedUnderSection(sidebarRows, j)) {
                    hasRootBetween = true;
                    break;
                }
            }
            if (!hasRootBetween) {
                zones.push({ rowIndex: sIdx, id: `__dropzone:${sIdx}`, position: 'before' });
            }
        }
        // After the last section if no root-level items follow it.
        if (sectionIndices.length > 0) {
            const lastSectionIdx = sectionIndices[sectionIndices.length - 1];
            let hasRootAfter = false;
            for (let j = lastSectionIdx + 1; j < sidebarRows.length; j++) {
                const row = sidebarRows[j];
                if (row.kind === 'break') { hasRootAfter = true; break; }
                if (row.kind === 'item' && !isSidebarItemNestedUnderSection(sidebarRows, j)) {
                    hasRootAfter = true;
                    break;
                }
            }
            if (!hasRootAfter) {
                zones.push({ rowIndex: sidebarRows.length, id: '__dropzone:end', position: 'end' });
            }
        }
        return zones;
    }, [sidebarRows]);

    return (
        <div className={`${prefix}-sidebar-dnd px-1`}>
            <DndContext
                sensors={sensors}
                collisionDetection={verticalClosestCollision}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEndWrapped}
                onDragCancel={handleDragCancel}
            >
                <SortableContext
                    items={sortableIds}
                    strategy={verticalListSortingStrategy}
                >
                    <div
                        className={cn(
                            `${prefix}-sidebar-sortable`,
                            'flex flex-col gap-2'
                        )}
                    >
                        {map(sidebarRows, (row, rowIndex) => {
                            // Break rows are invisible structural markers — skip rendering.
                            if (row.kind === 'break') return null;
                            const sortId = compositeEditQuestionSidebarSortableId(row, rowIndex);
                            const isOverTarget = overDragId === sortId && activeDragId !== sortId;
                            const isNested = row.kind === 'item' && (nestingMap.get(rowIndex) ?? false);

                            // Render a drop zone before this section if one is registered.
                            const zoneForRow = dropZonePositions.find((z) => z.rowIndex === rowIndex);

                            if (row.kind === 'section') {
                                return (
                                    <React.Fragment key={sortId}>
                                        {zoneForRow && (
                                            <SectionDropZone
                                                id={zoneForRow.id}
                                                prefix={prefix}
                                                isActive={!!activeDragId}
                                                dropHereLabel={dropHereLabel}
                                            />
                                        )}
                                        {isOverTarget && dropDirection === 'before' && dropIndicator(false)}
                                        <SortableSectionRow
                                            prefix={prefix}
                                            sectionId={row.section.id}
                                            sectionTitle={row.section.title}
                                            descPreview={sectionSidebarDescriptionPreview(row.section.description)}
                                            descPlainText={tiptapDocToPlainText(row.section.description)}
                                            isSelected={selectedSectionId === row.section.id}
                                            disabled={disabled}
                                            removeLabel={removeSidebarSectionLabel}
                                            onSelectSection={stableSelectSection}
                                            onRemoveSection={stableRemoveSection}
                                            untitledSectionLabel={untitledSectionLabel}
                                        />
                                        {isOverTarget && dropDirection === 'after' && dropIndicator(false)}
                                    </React.Fragment>
                                );
                            }
                            const indexInItems = itemIndexMap.get(row.itemId);
                            if (indexInItems == null) {
                                return null;
                            }
                            return (
                                <React.Fragment key={sortId}>
                                    {isOverTarget && dropDirection === 'before' && dropIndicator(isNested)}
                                    <SortableSidebarRow
                                        prefix={prefix}
                                        itemId={row.itemId}
                                        label={getItemLabel(row.itemId, indexInItems, items.length)}
                                        isSelected={row.itemId === selectedId}
                                        disabled={disabled}
                                        hasValidationError={itemHasValidationError?.(indexInItems)}
                                        removeDisabled={removeBlocked}
                                        removeLabel={removeItemLabel}
                                        nestedUnderSection={isNested}
                                        onSelectItem={stableSelectItem}
                                        onRemoveItem={stableRemoveItem}
                                    />
                                    {isOverTarget && dropDirection === 'after' && dropIndicator(isNested)}
                                </React.Fragment>
                            );
                        })}
                        {/* Drop zone after the last section */}
                        {dropZonePositions.find((z) => z.position === 'end') && (
                            <SectionDropZone
                                id="__dropzone:end"
                                prefix={prefix}
                                isActive={!!activeDragId}
                                dropHereLabel={dropHereLabel}
                            />
                        )}
                    </div>
                </SortableContext>
            </DndContext>
        </div>
    );
});

export type CompositeEditSortableQuestionsLayoutProps = {
    prefix: string;
    className?: string;
    sidebarWidthClassName?: string;

    /** Sidebar title before count, e.g. `"Exercises"` → `Exercises (3)` */
    listTitle: string;
    emptyMessage: string;

    items: readonly { id: string }[];
    selectedId: string | null;
    onSelectId: (id: string | null) => void;
    /** When set, right column edits section title/description (same pattern as a question). */
    selectedSectionId: string | null;
    onSelectSectionId: (id: string | null) => void;

    disabled?: boolean;
    /** Disable remove when `items.length <= minItemsToKeep` (default `0`). */
    minItemsToKeep?: number;

    onRemoveItem: (itemId: string) => void;

    /** Returns a plain string label for the sidebar preview, e.g. `"1. Trắc nghiệm"`. */
    getItemLabel: (itemId: string, index: number, total: number) => string;
    itemHasValidationError?: (index: number) => boolean;

    addButtonLabel: string;
    addButtonOptions: CompositeEditAddQuestionOption[];
    onAddItem: (typeId: string) => void;
    addButtonClassName?: string;

    /** Default detail heading before index (1-based display). Ignored if `renderDetailHeader` set. */
    detailTitle?: string;
    detailBadge?: React.ReactNode;
    renderDetailHeader?: (ctx: { itemId: string; index: number }) => React.ReactNode;
    /**
     * Body for each question/prompt row. When set, small screens render every
     * item (and section editors) stacked so the hidden sidebar is not required.
     */
    renderItemDetail?: (ctx: { itemId: string; index: number; total: number }) => React.ReactNode;
    detailContent: React.ReactNode;
    detailEmptyMessage: string;

    removeItemLabel?: string;

    /** Detail panel heading when a section is selected. */
    sectionDetailTitle?: string;
    sectionDetailBadge?: React.ReactNode;

    /**
     * Resolved sidebar rows (sections + items). Use {@link resolvedQuestionsSidebarRows} with stored
     * payload and current item ids.
     */
    sidebarRows: CompositeEditQuestionSidebarRow[];
    onSidebarRowsChange: (rows: CompositeEditQuestionSidebarRow[]) => void;
    onAddSidebarSection: () => void;
    addSidebarSectionLabel?: string;
    removeSidebarSectionLabel?: string;
    onRemoveSidebarSection?: (sectionId: string) => void;
    /** Rich text toolbar / table bubble i18n */
    richTextTranslate?: KitTranslateFn;
    /** Label shown when section title is empty */
    untitledSectionLabel?: string;
    /** Label for section title field */
    sectionTitleLabel?: string;
    /** Placeholder for section title input */
    sectionTitlePlaceholder?: string;
    /** Label for section description field */
    sectionDescriptionLabel?: string;
    /** Label for drop zone */
    dropHereLabel?: string;
};

/**
 * Questions tab with sortable sidebar rows, add button, and detail panel.
 * Composes {@link CompositeEditQuestionsLayout} split mode.
 */
export function CompositeEditSortableQuestionsLayout({
    prefix,
    className,
    sidebarWidthClassName,
    listTitle,
    emptyMessage,
    items,
    selectedId,
    onSelectId,
    selectedSectionId,
    onSelectSectionId,
    disabled = false,
    minItemsToKeep = 0,
    onRemoveItem,
    getItemLabel,
    itemHasValidationError,
    addButtonLabel,
    addButtonOptions,
    onAddItem,
    addButtonClassName,
    detailTitle = 'Exercise',
    detailBadge,
    renderDetailHeader,
    renderItemDetail,
    detailContent,
    detailEmptyMessage,
    removeItemLabel = 'Remove item',
    sectionDetailTitle = 'Section',
    sectionDetailBadge,
    sidebarRows,
    onSidebarRowsChange,
    onAddSidebarSection,
    addSidebarSectionLabel = 'Add section',
    removeSidebarSectionLabel = 'Remove section',
    onRemoveSidebarSection,
    richTextTranslate,
    untitledSectionLabel = 'Untitled section',
    sectionTitleLabel = 'Title',
    sectionTitlePlaceholder = 'Section title',
    sectionDescriptionLabel = 'Description',
    dropHereLabel = 'Drop here',
}: CompositeEditSortableQuestionsLayoutProps): React.ReactElement {
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );



    // Use refs for values that change frequently to keep callbacks stable and avoid
    // cascading re-renders of every SortableItem during drag operations.
    const sidebarRowsRef = React.useRef(sidebarRows);
    sidebarRowsRef.current = sidebarRows;
    const onSidebarRowsChangeRef = React.useRef(onSidebarRowsChange);
    onSidebarRowsChangeRef.current = onSidebarRowsChange;
    const onRemoveSidebarSectionRef = React.useRef(onRemoveSidebarSection);
    onRemoveSidebarSectionRef.current = onRemoveSidebarSection;

    const handleSidebarDragEnd = React.useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;
            if (!over || active.id === over.id) {
                return;
            }
            const rows = sidebarRowsRef.current;

            // --- Handle drops on between-section drop zones ---
            const overId = String(over.id);
            if (overId.startsWith('__dropzone:')) {
                const ids = map(rows, (r, i) => compositeEditQuestionSidebarSortableId(r, i));
                const oldIndex = ids.indexOf(String(active.id));
                if (oldIndex < 0) return;

                // Remove the item from its current position.
                const next = [...rows];
                const [movedRow] = next.splice(oldIndex, 1);

                if (overId === '__dropzone:end') {
                    // Append break + item at the end of the list.
                    next.push({ kind: 'break' }, movedRow);
                } else {
                    // Insert before a specific section.
                    const targetRowIndex = parseInt(overId.replace('__dropzone:', ''), 10);
                    if (isNaN(targetRowIndex)) return;
                    // After splice, indices shift if oldIndex < targetRowIndex.
                    const insertAt = oldIndex < targetRowIndex ? targetRowIndex - 1 : targetRowIndex;
                    next.splice(insertAt, 0, { kind: 'break' }, movedRow);
                }

                // Clean up redundant breaks.
                const cleaned = filter(next, (row, i) => {
                    if (row.kind !== 'break') return true;
                    if (i === 0) return false;
                    const prev = next[i - 1];
                    if (prev?.kind === 'break') return false;
                    const nextRow = next[i + 1];
                    if (!nextRow || nextRow.kind === 'section') return false;
                    return true;
                });

                onSidebarRowsChangeRef.current(cleaned);
                return;
            }

            // --- Standard sortable reorder ---
            const ids = map(rows, (r, i) => compositeEditQuestionSidebarSortableId(r, i));
            const oldIndex = ids.indexOf(String(active.id));
            const newIndex = ids.indexOf(overId);
            if (oldIndex < 0 || newIndex < 0) {
                return;
            }

            const movedRow = rows[oldIndex];
            const wasNested = movedRow.kind === 'item' && isSidebarItemNestedUnderSection(rows, oldIndex);

            let next = arrayMove([...rows], oldIndex, newIndex);

            // When an item was nested and is dragged upward past its section header,
            // it would fall into the previous section. Insert a break to un-nest it.
            if (movedRow.kind === 'item' && wasNested && newIndex < oldIndex) {
                const newPos = next.findIndex((r) => r.kind === 'item' && r.itemId === movedRow.itemId);
                if (newPos >= 0 && isSidebarItemNestedUnderSection(next, newPos)) {
                    // Insert a break before this item to terminate the previous section.
                    next.splice(newPos, 0, { kind: 'break' });
                }
            }

            // Clean up redundant breaks:
            // - breaks at index 0 (nothing to terminate)
            // - breaks directly before a section (section already starts a new group)
            // - consecutive breaks
            // - breaks at the end with no items after
            next = filter(next, (row, i) => {
                if (row.kind !== 'break') return true;
                if (i === 0) return false;
                const prev = next[i - 1];
                if (prev?.kind === 'break') return false;
                const nextRow = next[i + 1];
                if (!nextRow || nextRow.kind === 'section') return false;
                return true;
            });

            onSidebarRowsChangeRef.current(next);
        },
        []
    );

    const handlePatchSection = React.useCallback(
        (sectionId: string, update: { title?: string; description?: TiptapJSON }) => {
            onSidebarRowsChangeRef.current(
                map(sidebarRowsRef.current, (row) =>
                    row.kind === 'section' && row.section.id === sectionId
                        ? { kind: 'section', section: { ...row.section, ...update } }
                        : row
                )
            );
        },
        []
    );

    const handleRemoveSection = React.useCallback(
        (sectionId: string) => {
            if (onRemoveSidebarSectionRef.current) {
                onRemoveSidebarSectionRef.current(sectionId);
                return;
            }
            onSidebarRowsChangeRef.current(
                filter(sidebarRowsRef.current, (r) => !(r.kind === 'section' && r.section.id === sectionId))
            );
        },
        []
    );

    const sortableIds = React.useMemo(
        () => sidebarRows
            .map((r, i) => ({ r, i }))
            .filter(({ r }) => r.kind !== 'break')
            .map(({ r, i }) => compositeEditQuestionSidebarSortableId(r, i)),
        [sidebarRows]
    );

    const selectedSection = React.useMemo(() => {
        if (!selectedSectionId) {
            return null;
        }
        const row = sidebarRows.find(
            (r) => r.kind === 'section' && r.section.id === selectedSectionId
        );
        return row?.kind === 'section' ? row.section : null;
    }, [selectedSectionId, sidebarRows]);

    React.useEffect(() => {
        if (selectedSectionId && !selectedSection) {
            onSelectSectionId(null);
        }
    }, [onSelectSectionId, selectedSection, selectedSectionId]);

    const selectedIndex = selectedId ? items.findIndex((i) => i.id === selectedId) : -1;
    const removeBlocked = items.length <= minItemsToKeep;

    const hasSections = sidebarRows.some((r) => r.kind === 'section');
    const showSidebarEmpty = items.length === 0 && !hasSections;

    const rowsForMobile = React.useMemo(
        () =>
            sidebarRows.length > 0
                ? sidebarRows
                : map(items, (item) => ({ kind: 'item' as const, itemId: item.id })),
        [items, sidebarRows]
    );

    const renderSectionDetailFields = (
        section: CompositeEditQuestionSidebarSection
    ): React.ReactElement => (
        <div className={cn(`${prefix}-section-detail-fields`, 'space-y-4 min-w-0')}>
            <div className={cn(`${prefix}-section-detail-title-field`, 'space-y-2')}>
                <Label
                    htmlFor={`${prefix}-section-detail-title-${section.id}`}
                    className="text-sm text-muted-foreground"
                >
                    {sectionTitleLabel}
                </Label>
                <Input
                    id={`${prefix}-section-detail-title-${section.id}`}
                    value={section.title}
                    onChange={(e) => handlePatchSection(section.id, { title: e.target.value })}
                    disabled={disabled}
                    placeholder={sectionTitlePlaceholder}
                    className={cn(`${prefix}-section-detail-title-input`)}
                />
            </div>
            <div className={cn(`${prefix}-section-detail-description-field`, 'space-y-2')}>
                <span
                    className={cn(
                        `${prefix}-section-detail-description-label`,
                        'text-sm text-muted-foreground'
                    )}
                >
                    {sectionDescriptionLabel}
                </span>
                <RichTextEditor
                    value={section.description ?? createEmptyDocument()}
                    onChange={(d) => handlePatchSection(section.id, { description: d })}
                    readOnly={disabled}
                    minHeight={200}
                    className={cn(`${prefix}-section-detail-rich-text`, 'text-sm')}
                    translate={richTextTranslate}
                />
            </div>
        </div>
    );

    const renderSectionHeadingRow = (): React.ReactElement => (
        <div className={cn(`${prefix}-detail-heading`, 'flex items-center gap-2 flex-wrap')}>
            <h3
                className={cn(
                    `${prefix}-detail-heading-title`,
                    'text-lg font-semibold text-foreground'
                )}
            >
                {sectionDetailTitle}
            </h3>
            {sectionDetailBadge ? (
                <span className={cn(`${prefix}-detail-heading-badge`, 'inline-flex items-center')}>
                    {sectionDetailBadge}
                </span>
            ) : (
                <Badge
                    variant="outline"
                    className={cn(
                        `${prefix}-section-detail-type-badge`,
                        'border-primary/25 bg-primary/10'
                    )}
                >
                    Section
                </Badge>
            )}
        </div>
    );

    const mobileStackDetail =
        renderItemDetail == null ? null : rowsForMobile.length === 0 ? (
            <p
                className={cn(
                    `${prefix}-detail-empty`,
                    'py-12 text-center text-sm text-muted-foreground'
                )}
            >
                {detailEmptyMessage}
            </p>
        ) : (
            <div className={cn(`${prefix}-questions-mobile-stack`, 'space-y-6')}>
                {map(rowsForMobile, (row, rowIndex) => {
                    // Break rows are invisible structural markers — skip rendering.
                    if (row.kind === 'break') return null;
                    const sortKey = compositeEditQuestionSidebarSortableId(row, rowIndex);
                    if (row.kind === 'section') {
                        return (
                            <div
                                key={sortKey}
                                className={cn(
                                    `${prefix}-mobile-stack-section`,
                                    'space-y-4 rounded-sm border border-border bg-muted/30 p-4'
                                )}
                            >
                                <div
                                    className={cn(
                                        `${prefix}-mobile-stack-section-toolbar`,
                                        'flex items-start justify-between gap-2'
                                    )}
                                >
                                    <div
                                        className={cn(
                                            `${prefix}-mobile-stack-section-heading`,
                                            'flex min-w-0 flex-1 flex-wrap items-center gap-2'
                                        )}
                                    >
                                        {renderSectionHeadingRow()}
                                    </div>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className={cn(
                                            `${prefix}-mobile-stack-section-remove`,
                                            'h-8 w-8 shrink-0 cursor-pointer text-muted-foreground hover:text-foreground'
                                        )}
                                        disabled={disabled}
                                        aria-label={removeSidebarSectionLabel}
                                        onClick={() => {
                                            if (selectedSectionId === row.section.id) {
                                                onSelectSectionId(null);
                                            }
                                            handleRemoveSection(row.section.id);
                                        }}
                                    >
                                        <X
                                            className={cn(
                                                `${prefix}-mobile-stack-section-remove-icon`,
                                                'h-4 w-4'
                                            )}
                                        />
                                    </Button>
                                </div>
                                {renderSectionDetailFields(row.section)}
                            </div>
                        );
                    }
                    const indexInItems = items.findIndex((i) => i.id === row.itemId);
                    if (indexInItems < 0) {
                        return null;
                    }
                    const nestedUnderSection = isSidebarItemNestedUnderSection(
                        rowsForMobile,
                        rowIndex
                    );
                    return (
                        <div
                            key={sortKey}
                            className={cn(
                                `${prefix}-mobile-stack-question`,
                                nestedUnderSection && `${prefix}-mobile-stack-question--nested`,
                                'space-y-4 rounded-sm border border-border bg-muted/30 p-4',
                                nestedUnderSection && 'ml-6'
                            )}
                        >
                            <div
                                className={cn(
                                    `${prefix}-mobile-stack-question-toolbar`,
                                    'flex items-start justify-between gap-2'
                                )}
                            >
                                <div className="min-w-0 flex-1">
                                    {renderDetailHeader ? (
                                        <div
                                            className={cn(
                                                `${prefix}-detail-heading`,
                                                'flex flex-wrap items-center gap-2'
                                            )}
                                        >
                                            {renderDetailHeader({
                                                itemId: row.itemId,
                                                index: indexInItems,
                                            })}
                                        </div>
                                    ) : (
                                        <div
                                            className={cn(
                                                `${prefix}-detail-heading`,
                                                'flex flex-wrap items-center gap-2'
                                            )}
                                        >
                                            <h3
                                                className={cn(
                                                    `${prefix}-detail-heading-title`,
                                                    'text-lg font-semibold text-foreground'
                                                )}
                                            >
                                                {detailTitle} {indexInItems + 1}
                                            </h3>
                                            {detailBadge ? (
                                                <span
                                                    className={cn(
                                                        `${prefix}-detail-heading-badge`,
                                                        'inline-flex items-center'
                                                    )}
                                                >
                                                    {detailBadge}
                                                </span>
                                            ) : null}
                                        </div>
                                    )}
                                </div>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className={cn(
                                        `${prefix}-mobile-stack-question-remove`,
                                        'h-8 w-8 shrink-0 cursor-pointer text-muted-foreground hover:text-foreground'
                                    )}
                                    disabled={disabled || removeBlocked}
                                    aria-label={removeItemLabel}
                                    onClick={() => onRemoveItem(row.itemId)}
                                >
                                    <X
                                        className={cn(
                                            `${prefix}-mobile-stack-question-remove-icon`,
                                            'h-4 w-4'
                                        )}
                                    />
                                </Button>
                            </div>
                            <div className={cn(`${prefix}-detail-content`, 'min-w-0')}>
                                {renderItemDetail({
                                    itemId: row.itemId,
                                    index: indexInItems,
                                    total: items.length,
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        );

    let detailInner: React.ReactElement;
    if (selectedSection) {
        detailInner = (
            <div
                className={cn(
                    `${prefix}-detail-inner`,
                    `${prefix}-detail-inner--section`,
                    'space-y-4 pb-6'
                )}
            >
                {renderSectionHeadingRow()}
                {renderSectionDetailFields(selectedSection)}
            </div>
        );
    } else if (selectedId != null && selectedIndex >= 0) {
        detailInner = (
            <div className={cn(`${prefix}-detail-inner`, 'space-y-4 pb-6')}>
                {renderDetailHeader ? (
                    <div
                        className={cn(
                            `${prefix}-detail-heading`,
                            'flex items-center gap-2 flex-wrap'
                        )}
                    >
                        {renderDetailHeader({ itemId: selectedId, index: selectedIndex })}
                    </div>
                ) : (
                    <div
                        className={cn(
                            `${prefix}-detail-heading`,
                            'flex items-center gap-2 flex-wrap'
                        )}
                    >
                        <h3
                            className={cn(
                                `${prefix}-detail-heading-title`,
                                'text-lg font-semibold text-foreground'
                            )}
                        >
                            {detailTitle} {selectedIndex + 1}
                        </h3>
                        {detailBadge ? (
                            <span
                                className={cn(
                                    `${prefix}-detail-heading-badge`,
                                    'inline-flex items-center'
                                )}
                            >
                                {detailBadge}
                            </span>
                        ) : null}
                    </div>
                )}
                <div className={cn(`${prefix}-detail-content`, 'min-w-0')}>
                    {renderItemDetail
                        ? renderItemDetail({
                              itemId: selectedId,
                              index: selectedIndex,
                              total: items.length,
                          })
                        : detailContent}
                </div>
            </div>
        );
    } else {
        detailInner = (
            <p
                className={cn(
                    `${prefix}-detail-empty`,
                    'text-sm text-muted-foreground text-center py-12'
                )}
            >
                {detailEmptyMessage}
            </p>
        );
    }

    return (
        <CompositeEditQuestionsLayout
            prefix={prefix}
            className={className}
            sidebarWidthClassName={sidebarWidthClassName}
            sidebarList={
                <>
                    <div
                        className={cn(
                            `${prefix}-sidebar-header`,
                            'flex items-center justify-between gap-2 mb-2'
                        )}
                    >
                        <span
                            className={cn(
                                `${prefix}-sidebar-header-title`,
                                'text-sm font-semibold text-foreground'
                            )}
                        >
                            {listTitle} ({items.length})
                        </span>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className={cn(`${prefix}-sidebar-add-section`, 'shrink-0 h-8 text-xs')}
                            disabled={disabled}
                            onClick={onAddSidebarSection}
                        >
                            {addSidebarSectionLabel}
                        </Button>
                    </div>
                    {showSidebarEmpty ? (
                        <p
                            className={cn(
                                `${prefix}-sidebar-empty`,
                                'text-sm text-muted-foreground py-4 text-center'
                            )}
                        >
                            {emptyMessage}
                        </p>
                    ) : (
                        <SidebarDndList
                            prefix={prefix}
                            sensors={sensors}
                            handleSidebarDragEnd={handleSidebarDragEnd}
                            sortableIds={sortableIds}
                            sidebarRows={sidebarRows}
                            items={items}
                            selectedId={selectedId}
                            selectedSectionId={selectedSectionId}
                            disabled={disabled}
                            removeBlocked={removeBlocked}
                            removeSidebarSectionLabel={removeSidebarSectionLabel}
                            removeItemLabel={removeItemLabel}
                            itemHasValidationError={itemHasValidationError}
                            onSelectId={onSelectId}
                            onSelectSectionId={onSelectSectionId}
                            onRemoveItem={onRemoveItem}
                            handleRemoveSection={handleRemoveSection}
                            getItemLabel={getItemLabel}
                            untitledSectionLabel={untitledSectionLabel}
                            dropHereLabel={dropHereLabel}
                        />
                    )}
                </>
            }
            sidebarFooter={
                <CompositeEditAddQuestionButton
                    className={cn(`${prefix}-add-question`, addButtonClassName)}
                    label={addButtonLabel}
                    options={addButtonOptions}
                    disabled={disabled}
                    onAdd={onAddItem}
                />
            }
            detail={
                <>
                    {renderItemDetail ? <div className="lg:hidden">{mobileStackDetail}</div> : null}
                    <div className={cn(renderItemDetail && 'hidden lg:block')}>{detailInner}</div>
                </>
            }
        />
    );
}
